import { PGlite } from "@electric-sql/pglite";
import type { ChatMessage, MCPTool, MCPResource, Thread } from "@/types";

// Use CDN-hosted assets so the browser receives the correct WASM MIME type.
// @note Keep this version in sync with package.json @electric-sql/pglite version
const PGLITE_ASSET_BASE = "https://cdn.jsdelivr.net/npm/@electric-sql/pglite@0.3.15/dist/";
const pgliteWasmUrl = `${PGLITE_ASSET_BASE}pglite.wasm`;
const pgliteDataUrl = `${PGLITE_ASSET_BASE}pglite.data`;

let dbPromise: Promise<PGlite> | null = null;

const compileWasmModule = async () => {
  const response = await fetch(pgliteWasmUrl);
  // Some dev servers serve WASM without the correct MIME type; compile from buffer to avoid MIME checks.
  const buffer = await response.arrayBuffer();
  return WebAssembly.compile(buffer);
};

const initDb = async (): Promise<PGlite> => {
  const [wasmModule, fsBundle] = await Promise.all([
    compileWasmModule(),
    fetch(pgliteDataUrl).then(res => res.blob()),
  ]);

  const instance = new PGlite("idb://chatmcp-db", {
    wasmModule,
    fsBundle,
  });

  await instance.waitReady;
  
  // Create threads table first
  await instance.exec(`
    CREATE TABLE IF NOT EXISTS threads (
      id TEXT PRIMARY KEY,
      server_id TEXT NOT NULL,
      name TEXT,
      created_at TEXT NOT NULL,
      updated_at TEXT NOT NULL,
      last_message_preview TEXT,
      is_archived BOOLEAN DEFAULT FALSE,
      participants JSONB DEFAULT '[]'::jsonb
    );
  `);
  await instance.exec(`CREATE INDEX IF NOT EXISTS threads_server_id_idx ON threads(server_id);`);
  await instance.exec(`CREATE INDEX IF NOT EXISTS threads_updated_at_idx ON threads(updated_at DESC);`);
  
  await instance.exec(`
    CREATE TABLE IF NOT EXISTS messages (
      id TEXT PRIMARY KEY,
      contact_id TEXT NOT NULL,
      thread_id TEXT,
      author TEXT NOT NULL,
      content TEXT,
      created_at TEXT NOT NULL,
      status TEXT,
      meta JSONB DEFAULT '{}'::jsonb,
      FOREIGN KEY (thread_id) REFERENCES threads(id) ON DELETE CASCADE
    );
  `);
  await instance.exec(`CREATE INDEX IF NOT EXISTS messages_contact_id_idx ON messages(contact_id);`);
  await instance.exec(`CREATE INDEX IF NOT EXISTS messages_thread_id_idx ON messages(thread_id);`);

  await instance.exec(`
    CREATE TABLE IF NOT EXISTS mcp_tools (
      server_id TEXT NOT NULL,
      name TEXT NOT NULL,
      description TEXT,
      input_schema JSONB,
      output_schema JSONB,
      source_server TEXT,
      enabled BOOLEAN DEFAULT TRUE,
      PRIMARY KEY (server_id, name)
    );
  `);

  await instance.exec(`
    CREATE TABLE IF NOT EXISTS mcp_resources (
      server_id TEXT NOT NULL,
      name TEXT NOT NULL,
      uri TEXT,
      description TEXT,
      mime_type TEXT,
      PRIMARY KEY (server_id, name)
    );
  `);

  await instance.exec(`
    CREATE TABLE IF NOT EXISTS profile_settings (
      id INTEGER PRIMARY KEY CHECK (id = 1),
      display_name TEXT,
      username TEXT
    );
  `);

  return instance;
};

const getDb = async (): Promise<PGlite> => {
  if (!dbPromise) {
    dbPromise = initDb();
  }
  return dbPromise;
};

const schemaSql = `
DROP TABLE IF EXISTS messages;
DROP TABLE IF EXISTS threads;
DROP TABLE IF EXISTS mcp_tools;
DROP TABLE IF EXISTS mcp_resources;
DROP TABLE IF EXISTS profile_settings;

CREATE TABLE IF NOT EXISTS threads (
  id TEXT PRIMARY KEY,
  server_id TEXT NOT NULL,
  name TEXT,
  created_at TEXT NOT NULL,
  updated_at TEXT NOT NULL,
  last_message_preview TEXT,
  is_archived BOOLEAN DEFAULT FALSE,
  participants JSONB DEFAULT '[]'::jsonb
);
CREATE INDEX IF NOT EXISTS threads_server_id_idx ON threads(server_id);
CREATE INDEX IF NOT EXISTS threads_updated_at_idx ON threads(updated_at DESC);

CREATE TABLE IF NOT EXISTS messages (
  id TEXT PRIMARY KEY,
  contact_id TEXT NOT NULL,
  thread_id TEXT,
  author TEXT NOT NULL,
  content TEXT,
  created_at TEXT NOT NULL,
  status TEXT,
  meta JSONB DEFAULT '{}'::jsonb,
  FOREIGN KEY (thread_id) REFERENCES threads(id) ON DELETE CASCADE
);
CREATE INDEX IF NOT EXISTS messages_contact_id_idx ON messages(contact_id);
CREATE INDEX IF NOT EXISTS messages_thread_id_idx ON messages(thread_id);

CREATE TABLE IF NOT EXISTS mcp_tools (
  server_id TEXT NOT NULL,
  name TEXT NOT NULL,
  description TEXT,
  input_schema JSONB,
  output_schema JSONB,
  source_server TEXT,
  enabled BOOLEAN DEFAULT TRUE,
  PRIMARY KEY (server_id, name)
);

CREATE TABLE IF NOT EXISTS mcp_resources (
  server_id TEXT NOT NULL,
  name TEXT NOT NULL,
  uri TEXT,
  description TEXT,
  mime_type TEXT,
  PRIMARY KEY (server_id, name)
);

CREATE TABLE IF NOT EXISTS profile_settings (
  id INTEGER PRIMARY KEY CHECK (id = 1),
  display_name TEXT,
  username TEXT
);
`;
// ========== Thread Management ==========

export const createThread = async (serverId: string, name?: string): Promise<Thread> => {
  const db = await getDb();
  const now = new Date().toISOString();
  const id = `thread-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  
  await db.query(
    `INSERT INTO threads (id, server_id, name, created_at, updated_at, participants)
     VALUES ($1, $2, $3, $4, $5, $6)`,
    [id, serverId, name || null, now, now, JSON.stringify([serverId])]
  );
  
  return {
    id,
    serverId,
    name,
    createdAt: now,
    updatedAt: now,
    participants: [serverId],
  };
};

export const loadThreads = async (serverId?: string): Promise<Thread[]> => {
  const db = await getDb();
  const query = serverId
    ? `SELECT id, server_id AS "serverId", name, created_at AS "createdAt",
              updated_at AS "updatedAt", last_message_preview AS "lastMessagePreview",
              is_archived AS "isArchived", participants
       FROM threads
       WHERE server_id = $1 AND (is_archived IS NULL OR is_archived = FALSE)
       ORDER BY updated_at DESC`
    : `SELECT id, server_id AS "serverId", name, created_at AS "createdAt",
              updated_at AS "updatedAt", last_message_preview AS "lastMessagePreview",
              is_archived AS "isArchived", participants
       FROM threads
       WHERE (is_archived IS NULL OR is_archived = FALSE)
       ORDER BY updated_at DESC`;

  const result = serverId
    ? await db.query(query, [serverId])
    : await db.query(query);

  return result.rows.map((row: unknown) => {
    const r = row as Thread & { participants: string | string[] };
    return {
      id: r.id,
      serverId: r.serverId,
      name: r.name ?? undefined,
      createdAt: r.createdAt,
      updatedAt: r.updatedAt,
      lastMessagePreview: r.lastMessagePreview ?? undefined,
      isArchived: r.isArchived ?? undefined,
      participants: Array.isArray(r.participants)
        ? r.participants
        : JSON.parse(r.participants || '[]'),
    };
  });
};

export const loadThreadById = async (threadId: string): Promise<Thread | null> => {
  const db = await getDb();
  const result: { rows: Thread[] } = await db.query(
    `SELECT id, server_id AS "serverId", name, created_at AS "createdAt",
            updated_at AS "updatedAt", last_message_preview AS "lastMessagePreview",
            is_archived AS "isArchived", participants
     FROM threads
     WHERE id = $1`,
    [threadId]
  );

  if (!result.rows || !result.rows.length) return null;

  const row = result.rows[0]!;
  return {
    id: row.id as string,
    serverId: row.serverId as string,
    name: row.name as string | undefined,
    createdAt: row.createdAt as string,
    updatedAt: row.updatedAt as string,
    lastMessagePreview: row.lastMessagePreview as string | undefined,
    isArchived: row.isArchived as boolean | undefined,
    participants: Array.isArray(row.participants)
      ? (row.participants as string[])
      : JSON.parse(typeof row.participants === 'string' ? row.participants : '[]'),
  };
};

export const updateThread = async (
  threadId: string, 
  updates: Partial<Pick<Thread, 'name' | 'lastMessagePreview' | 'isArchived'>>
): Promise<void> => {
  const db = await getDb();
  const now = new Date().toISOString();
  
  const setClauses: string[] = ['updated_at = $2'];
  const values: any[] = [threadId, now];
  let paramIndex = 3;
  
  if (updates.name !== undefined) {
    setClauses.push(`name = $${paramIndex}`);
    values.push(updates.name);
    paramIndex++;
  }
  
  if (updates.lastMessagePreview !== undefined) {
    setClauses.push(`last_message_preview = $${paramIndex}`);
    values.push(updates.lastMessagePreview);
    paramIndex++;
  }
  
  if (updates.isArchived !== undefined) {
    setClauses.push(`is_archived = $${paramIndex}`);
    values.push(updates.isArchived);
    paramIndex++;
  }
  
  await db.query(
    `UPDATE threads SET ${setClauses.join(', ')} WHERE id = $1`,
    values
  );
};

export const deleteThread = async (threadId: string): Promise<void> => {
  const db = await getDb();
  // CASCADE will delete associated messages
  await db.query(`DELETE FROM threads WHERE id = $1`, [threadId]);
};

// ========== Message Management ==========
export const loadThread = async (contactId: string): Promise<ChatMessage[]> => {
  const db = await getDb();
  const result: { rows: ChatMessage[] } = await db.query(
    `SELECT id, contact_id AS "contactId", thread_id AS "threadId", author, content, created_at AS "createdAt", status, meta
     FROM messages
     WHERE contact_id = $1
     ORDER BY created_at ASC`,
    [contactId],
  );
  return result.rows.map(row => ({
    id: row.id as string,
    contactId: row.contactId as string,
    threadId: row.threadId as string | undefined,
    author: row.author as ChatMessage["author"],
    content: row.content as string,
    createdAt: row.createdAt as string,
    status: row.status as ChatMessage["status"],
    tags: Array.isArray((row.meta as any)?.tags)
      ? ((row.meta as any).tags as unknown[]).map(String)
      : undefined,
    topic: typeof (row.meta as any)?.topic === "string" ? ((row.meta as any).topic as string) : undefined,
    meta: (row.meta as Record<string, unknown>) ?? {},
  }));
};

export const loadThreadMessages = async (threadId: string): Promise<ChatMessage[]> => {
  const db = await getDb();
  const result: { rows: ChatMessage[] } = await db.query(
    `SELECT id, contact_id AS "contactId", thread_id AS "threadId", author, content, created_at AS "createdAt", status, meta
     FROM messages
     WHERE thread_id = $1
     ORDER BY created_at ASC`,
    [threadId],
  );
  return result.rows.map(row => ({
    id: row.id as string,
    contactId: row.contactId as string,
    threadId: row.threadId as string | undefined,
    author: row.author as ChatMessage["author"],
    content: row.content as string,
    createdAt: row.createdAt as string,
    status: row.status as ChatMessage["status"],
    tags: Array.isArray((row.meta as any)?.tags)
      ? ((row.meta as any).tags as unknown[]).map(String)
      : undefined,
    topic: typeof (row.meta as any)?.topic === "string" ? ((row.meta as any).topic as string) : undefined,
    meta: (row.meta as Record<string, unknown>) ?? {},
  }));
};

const serializeMeta = (message: ChatMessage) => ({
  ...(message.meta ?? {}),
  ...(message.tags ? { tags: message.tags } : {}),
  ...(message.topic ? { topic: message.topic } : {}),
});

export const saveThread = async (contactId: string, messages: ChatMessage[]) => {
  const db = await getDb();
  await db.transaction(async tx => {
    await tx.query(`DELETE FROM messages WHERE contact_id = $1`, [contactId]);
    for (const message of messages) {
      await tx.query(
        `INSERT INTO messages (id, contact_id, thread_id, author, content, created_at, status, meta)
         VALUES ($1, $2, $3, $4, $5, $6, $7, $8)
         ON CONFLICT (id) DO UPDATE SET
           content = EXCLUDED.content,
           status = EXCLUDED.status,
           meta = EXCLUDED.meta,
           thread_id = EXCLUDED.thread_id`,
        [
          message.id,
          contactId,
          message.threadId || null,
          message.author,
          message.content,
          message.createdAt,
          message.status,
          serializeMeta(message),
        ],
      );
    }
  });
  
  // Update thread's lastMessagePreview if threadId exists
  if (messages.length > 0 && messages[messages.length - 1]?.threadId) {
    const lastMessage = messages[messages.length - 1]!;
    const preview = lastMessage.content.substring(0, 100);
    await updateThread(lastMessage.threadId!, { lastMessagePreview: preview });
  }
};

export const saveThreadMessages = async (threadId: string, messages: ChatMessage[]) => {
  const db = await getDb();
  await db.transaction(async tx => {
    await tx.query(`DELETE FROM messages WHERE thread_id = $1`, [threadId]);
    for (const message of messages) {
      await tx.query(
        `INSERT INTO messages (id, contact_id, thread_id, author, content, created_at, status, meta)
         VALUES ($1, $2, $3, $4, $5, $6, $7, $8)
         ON CONFLICT (id) DO UPDATE SET
           content = EXCLUDED.content,
           status = EXCLUDED.status,
           meta = EXCLUDED.meta`,
        [
          message.id,
          message.contactId,
          threadId,
          message.author,
          message.content,
          message.createdAt,
          message.status,
          serializeMeta(message),
        ],
      );
    }
  });
  
  // Update thread's lastMessagePreview
  if (messages.length > 0) {
    const lastMessage = messages[messages.length - 1]!;
    const preview = lastMessage.content.substring(0, 100);
    await updateThread(threadId, { lastMessagePreview: preview });
  }
};

export type ProfileSettings = {
  displayName: string;
  username: string;
};

const defaultProfile: ProfileSettings = {
  displayName: "You",
  username: "chatmcp-user",
};

export const loadProfileSettings = async (): Promise<ProfileSettings> => {
  const db = await getDb();
  const result = await db.query(`SELECT display_name AS "displayName", username FROM profile_settings WHERE id = 1`);
  if (!result.rows.length) return defaultProfile;
  const row = result.rows[0] as Partial<ProfileSettings>;
  return {
    displayName: row.displayName ?? defaultProfile.displayName,
    username: row.username ?? defaultProfile.username,
  };
};

export const saveProfileSettings = async (settings: ProfileSettings) => {
  const db = await getDb();
  await db.query(
    `INSERT INTO profile_settings (id, display_name, username)
     VALUES (1, $1, $2)
     ON CONFLICT (id) DO UPDATE SET
       display_name = EXCLUDED.display_name,
       username = EXCLUDED.username`,
    [settings.displayName, settings.username],
  );
};

export const saveTools = async (serverId: string, tools: MCPTool[]) => {
  const db = await getDb();
  await db.transaction(async tx => {
    await tx.query(`DELETE FROM mcp_tools WHERE server_id = $1`, [serverId]);
    for (const tool of tools) {
      await tx.query(
        `INSERT INTO mcp_tools (server_id, name, description, input_schema, output_schema, source_server, enabled)
         VALUES ($1, $2, $3, $4, $5, $6, $7)
         ON CONFLICT (server_id, name) DO UPDATE SET
           description = EXCLUDED.description,
           input_schema = EXCLUDED.input_schema,
           output_schema = EXCLUDED.output_schema,
           source_server = EXCLUDED.source_server,
           enabled = EXCLUDED.enabled`,
        [
          serverId,
          tool.name,
          tool.description ?? null,
          tool.inputSchema ?? {},
          tool.outputSchema ?? {},
          tool.sourceServer ?? tool.serverId ?? serverId,
          tool.enabled ?? true,
        ],
      );
    }
  });
};

export const loadTools = async (serverId: string): Promise<MCPTool[]> => {
  const db = await getDb();
  const result = await db.query(
    `SELECT name, description, input_schema AS "inputSchema", output_schema AS "outputSchema", source_server AS "sourceServer", enabled
     FROM mcp_tools
     WHERE server_id = $1
     ORDER BY name ASC`,
    [serverId],
  );
  return (result.rows as MCPTool[]).map(row => ({
    name: row.name as string,
    description: (row.description as string) ?? undefined,
    inputSchema: (row.inputSchema as Record<string, unknown>) ?? undefined,
    outputSchema: (row.outputSchema as Record<string, unknown>) ?? undefined,
    sourceServer: (row.sourceServer as string) ?? undefined,
    serverId,
    enabled: row.enabled ?? true,
  }));
};

export const saveResources = async (serverId: string, resources: MCPResource[]) => {
  const db = await getDb();
  await db.transaction(async tx => {
    await tx.query(`DELETE FROM mcp_resources WHERE server_id = $1`, [serverId]);
    for (const resource of resources) {
      await tx.query(
        `INSERT INTO mcp_resources (server_id, name, uri, description, mime_type)
         VALUES ($1, $2, $3, $4, $5)
         ON CONFLICT (server_id, name) DO UPDATE SET
           uri = EXCLUDED.uri,
           description = EXCLUDED.description,
           mime_type = EXCLUDED.mime_type`,
        [
          serverId,
          resource.name,
          resource.uri ?? null,
          resource.description ?? null,
          resource.mimeType ?? null,
        ],
      );
    }
  });
};

export const loadResources = async (serverId: string): Promise<MCPResource[]> => {
  const db = await getDb();
  const result = await db.query(
    `SELECT name, uri, description, mime_type AS "mimeType"
     FROM mcp_resources
     WHERE server_id = $1
     ORDER BY name ASC`,
    [serverId],
  );
  return (result.rows as MCPResource[]).map(row => ({
    name: row.name as string,
    uri: (row.uri as string) ?? undefined,
    description: (row.description as string) ?? undefined,
    mimeType: (row.mimeType as string) ?? undefined,
    serverId,
  }));
};

export const resetDatabase = async () => {
  const db = await getDb();
  await db.exec(schemaSql);
};

export const seedDatabase = async () => {
  const db = await getDb();
  await db.exec(schemaSql);
  await db.exec(`
    INSERT INTO profile_settings (id, display_name, username)
    VALUES (1, 'You', 'chatmcp-user')
    ON CONFLICT (id) DO NOTHING;

    INSERT INTO messages (id, contact_id, author, content, created_at, status)
    VALUES
      ('seed-1', 'atlas', 'agent', 'Welcome to Atlas!', '${new Date().toISOString()}', 'sent'),
      ('seed-2', 'atlas', 'user', 'Hello!', '${new Date().toISOString()}', 'sent')
    ON CONFLICT (id) DO NOTHING;

    INSERT INTO mcp_tools (server_id, name, description, input_schema, output_schema, source_server, enabled)
    VALUES
      ('atlas', 'search', 'Search the knowledge base', '{}', '{}', 'Atlas', TRUE),
      ('atlas', 'summarize', 'Summarize text', '{}', '{}', 'Atlas', TRUE),
      ('talos', 'deploy', 'Deploy infra', '{}', '{}', 'Talos', FALSE)
    ON CONFLICT (server_id, name) DO NOTHING;
  `);
};

export const getDbStats = async () => {
  const db = await getDb();
  type CountRow = { count: number };
  const messagesResult = await db.query<CountRow>(`SELECT count(*) AS count FROM messages`);
  // Count enabled tools and total tools in a single query
  const toolsResult = await db.query<{ count: number; enabled_count: number }>(
    `SELECT 
       COUNT(*) AS count, 
       SUM(CASE WHEN enabled = TRUE THEN 1 ELSE 0 END) AS enabled_count 
     FROM mcp_tools`
  );
  const profilesResult = await db.query<CountRow>(`SELECT count(*) AS count FROM profile_settings`);
  
  return {
    messages: {
      count: Number(messagesResult.rows[0]?.count ?? 0),
      total: Number(messagesResult.rows[0]?.count ?? 0),
    },
    tools: {
      count: Number(toolsResult.rows[0]?.enabled_count ?? 0),
      total: Number(toolsResult.rows[0]?.count ?? 0),
    },
    profiles: {
      count: Number(profilesResult.rows[0]?.count ?? 0),
      total: Number(profilesResult.rows[0]?.count ?? 0),
    },
  };
};
